/**
 * Conector directo WebUSB para impresoras térmicas ESC/POS (Epson, Star, Xprinter, etc.)
 * Funciona en Google Chrome, Microsoft Edge, Opera y Brave sobre Windows, Mac, Linux y Android.
 */

export interface UsbPrinterInfo {
  productName: string;
  manufacturerName: string;
  vendorId: number;
  productId: number;
  serialNumber?: string;
}

export class WebUsbPrinter {
  private device: USBDevice | null = null;
  private endpointNumber: number | null = null;
  private interfaceNumber: number | null = null;

  /** Verifica si la API WebUSB está disponible en el navegador */
  public static isSupported(): boolean {
    return typeof navigator !== "undefined" && "usb" in navigator;
  }

  /** Devuelve la información de la impresora USB actualmente conectada */
  public getInfo(): UsbPrinterInfo | null {
    if (!this.device) return null;
    return {
      productName: this.device.productName || "Impresora Térmica USB",
      manufacturerName: this.device.manufacturerName || "Genérica",
      vendorId: this.device.vendorId,
      productId: this.device.productId,
      serialNumber: this.device.serialNumber,
    };
  }

  public isConnected(): boolean {
    return this.device !== null && this.device.opened && this.endpointNumber !== null;
  }

  /**
   * Abre el selector nativo del navegador para que el usuario vincule su impresora USB.
   */
  public async requestAndConnect(): Promise<UsbPrinterInfo> {
    if (!WebUsbPrinter.isSupported()) {
      throw new Error("WebUSB no es compatible con este navegador. Usa Google Chrome o Microsoft Edge.");
    }

    try {
      // Solicitamos acceso a dispositivos USB sin filtro estricto para aceptar POS chinos y de marca
      const device = await navigator.usb.requestDevice({ filters: [] });
      await this.connectDevice(device);
      const info = this.getInfo();
      if (!info) throw new Error("No se pudo obtener información del dispositivo conectado");
      return info;
    } catch (err) {
      this.device = null;
      throw err;
    }
  }

  /**
   * Intenta reconectar silenciosamente a una impresora previamente vinculada por el usuario.
   */
  public async autoReconnect(preferredVendorId?: number, preferredProductId?: number): Promise<boolean> {
    if (!WebUsbPrinter.isSupported()) return false;

    try {
      const devices = await navigator.usb.getDevices();
      if (devices.length === 0) return false;

      let targetDevice: USBDevice | undefined;
      if (preferredVendorId && preferredProductId) {
        targetDevice = devices.find(
          (d) => d.vendorId === preferredVendorId && d.productId === preferredProductId,
        );
      }
      if (!targetDevice) {
        targetDevice = devices[0];
      }

      await this.connectDevice(targetDevice);
      return this.isConnected();
    } catch {
      return false;
    }
  }

  /** Configura interfaces y localiza el endpoint de salida (OUT bulk) */
  private async connectDevice(device: USBDevice): Promise<void> {
    this.device = device;
    if (!this.device.opened) {
      await this.device.open();
    }

    if (this.device.configuration === null) {
      await this.device.selectConfiguration(1);
    }

    const interfaces = this.device.configuration?.interfaces || [];
    let foundOutEndpoint: USBEndpoint | null = null;
    let foundInterfaceNumber: number | null = null;

    // Buscar una interfaz con un endpoint de dirección OUT (datos hacia la impresora)
    for (const iface of interfaces) {
      for (const alternate of iface.alternates) {
        const outEndpoint = alternate.endpoints.find((ep) => ep.direction === "out");
        if (outEndpoint) {
          foundOutEndpoint = outEndpoint;
          foundInterfaceNumber = iface.interfaceNumber;
          break;
        }
      }
      if (foundOutEndpoint) break;
    }

    if (!foundOutEndpoint || foundInterfaceNumber === null) {
      await this.device.close();
      this.device = null;
      throw new Error("No se encontró canal de comunicación de salida (OUT Endpoint) en este dispositivo USB.");
    }

    await this.device.claimInterface(foundInterfaceNumber);
    this.interfaceNumber = foundInterfaceNumber;
    this.endpointNumber = foundOutEndpoint.endpointNumber;
  }

  /**
   * Envía un búfer binario de comandos ESC/POS directamente a la impresora.
   */
  public async send(data: Uint8Array): Promise<void> {
    if (!this.isConnected() || !this.device || this.endpointNumber === null) {
      throw new Error("La impresora USB no está conectada");
    }

    // Dividir en fragmentos de 512 bytes para evitar saturar el búfer de entrada de la impresora
    const CHUNK_SIZE = 512;
    for (let offset = 0; offset < data.length; offset += CHUNK_SIZE) {
      const chunk = data.slice(offset, offset + CHUNK_SIZE);
      const result = await this.device.transferOut(this.endpointNumber, chunk);
      if (result.status !== "ok") {
        throw new Error(`Error al enviar datos a la impresora USB: ${result.status}`);
      }
    }
  }

  /** Cierra la conexión USB de forma segura */
  public async disconnect(): Promise<void> {
    if (this.device && this.device.opened) {
      try {
        if (this.interfaceNumber !== null) {
          await this.device.releaseInterface(this.interfaceNumber);
        }
        await this.device.close();
      } catch {
        // Ignorar errores al cerrar
      }
    }
    this.device = null;
    this.interfaceNumber = null;
    this.endpointNumber = null;
  }
}
